import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaFileUpload, FaEye, FaCheckCircle } from 'react-icons/fa';
import { IMAGE_URL } from '../../../Api/commonAPI';

const SectionG = ({ formData: parentFormData, onDataChange }) => {
    // Initialize documents from parentFormData
    const initialDocuments = parentFormData && Object.keys(parentFormData).length > 0
        ? parentFormData
        : {
            AadharCardF: null,
            AadharCardB: null,
            PRANCard: null,
            PANCard: null,
            Passbook: null
        };

    const [documents, setDocuments] = useState(initialDocuments);
    const [viewImage, setViewImage] = useState(null);
    const [viewImageName, setViewImageName] = useState('');
    const [dragActive, setDragActive] = useState({});
    const [uploadProgress, setUploadProgress] = useState({});
    const [errors, setErrors] = useState({});

    // Refs to prevent circular updates
    const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
    const isUpdatingFromParentRef = useRef(false);
    const lastSentToParentRef = useRef(JSON.stringify(documents));
    const fileInputRefs = useRef({});

    const documentFields = [
        { key: 'AadharCardF', label: 'Aadhar Card (Front Side)', required: true },
        { key: 'AadharCardB', label: 'Aadhar Card (Back Side)', required: true },
        { key: 'PRANCard', label: 'PRAN Card', required: true },
        { key: 'PANCard', label: 'Pan Card', required: true },
        { key: 'Passbook', label: 'Cancel Check Image or Passbook Photo', required: true }
    ];

    // Update parent when documents change
    useEffect(() => {
        if (isUpdatingFromParentRef.current) {
            return;
        }

        const currentData = JSON.stringify(documents);
        if (currentData !== lastSentToParentRef.current) {
            lastSentToParentRef.current = currentData;

            if (onDataChange) {
                onDataChange(documents);
            }
        }
    }, [documents, onDataChange]);

    // Update from parent data (API response)
    useEffect(() => {
        if (!parentFormData) {
            return;
        }

        const currentParentData = JSON.stringify(parentFormData);
        if (currentParentData === lastParentDataRef.current) {
            return;
        }

        const currentDocuments = JSON.stringify(documents);
        const newDocuments = JSON.stringify(parentFormData);

        if (currentDocuments !== newDocuments) {
            isUpdatingFromParentRef.current = true;
            setDocuments(parentFormData);
            lastParentDataRef.current = currentParentData;

            setTimeout(() => {
                isUpdatingFromParentRef.current = false;
            }, 100);
        }
    }, [parentFormData]);

    // Validate file
    const validateFile = (file) => {
        const errors = {};

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            errors.type = 'Please upload only JPG, PNG or PDF files';
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            errors.size = 'File size should not exceed 5MB';
        }

        return errors;
    };

    // Handle file upload with progress simulation
    const handleFileUpload = useCallback((key, file) => {
        if (!file) return;

        const validationErrors = validateFile(file);
        if (Object.keys(validationErrors).length > 0) {
            const errorMessage = Object.values(validationErrors).join(', ');
            setErrors(prev => ({ ...prev, [key]: errorMessage }));
            alert(errorMessage);
            return;
        }

        // Clear previous errors
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });

        // Simulate upload progress
        setUploadProgress(prev => ({ ...prev, [key]: 0 }));

        const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
                const currentProgress = prev[key] || 0;
                if (currentProgress >= 100) {
                    clearInterval(progressInterval);
                    return prev;
                }
                return { ...prev, [key]: Math.min(currentProgress + 20, 100) };
            });
        }, 100);

        // Set file after validation
        setTimeout(() => {
            setDocuments(prev => ({
                ...prev,
                [key]: file
            }));
            setUploadProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[key];
                return newProgress;
            });
            clearInterval(progressInterval);
        }, 500);
    }, []);

    // Handle file input change
    const handleFileChange = (key, event) => {
        const file = event.target.files[0];
        if (file) {
            handleFileUpload(key, file);
        }
        // Reset input to allow same file selection
        if (event.target) {
            event.target.value = '';
        }
    };

    // Handle drag events
    const handleDrag = useCallback((e, key) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(prev => ({ ...prev, [key]: true }));
        } else if (e.type === 'dragleave') {
            setDragActive(prev => ({ ...prev, [key]: false }));
        }
    }, []);

    // Handle drop
    const handleDrop = useCallback((e, key) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(prev => ({ ...prev, [key]: false }));

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            handleFileUpload(key, file);
        }
    }, [handleFileUpload]);

    // Handle remove file
    const handleRemoveFile = (key) => {
        if (window.confirm('Are you sure you want to remove this file?')) {
            setDocuments(prev => ({
                ...prev,
                [key]: null
            }));
            // Reset file input
            const input = fileInputRefs.current[key];
            if (input) {
                input.value = '';
            }
            // Clear errors
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };

    // Handle view image
    const handleViewImage = (url, fileName) => {
        if (url) {
            setViewImage(url);
            setViewImageName(fileName || 'Document');
        }
    };

    const handleCloseImageView = () => {
        setViewImage(null);
        setViewImageName('');
    };

    // Helper function to check if document is a URL or File object
    const getDocumentUrl = (doc) => {
        if (!doc) return null;
        if (typeof doc === 'string') {
            // It's a URL
            return doc.startsWith('http') ? doc : `${IMAGE_URL}${doc}`;
        }
        if (doc instanceof File) {
            // It's a File object
            return URL.createObjectURL(doc);
        }
        return null;
    };

    // Helper function to check if document is an image
    const isImage = (doc) => {
        if (!doc) return false;
        if (typeof doc === 'string') {
            // URL - check extension
            return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(doc);
        }
        if (doc instanceof File) {
            return doc.type.startsWith('image/');
        }
        return false;
    };

    // Helper function to get document name
    const getDocumentName = (doc, fieldKey) => {
        if (!doc) return '';
        if (doc instanceof File) {
            return doc.name;
        }
        if (typeof doc === 'string') {
            // Extract filename from URL
            const urlParts = doc.split('/');
            return urlParts[urlParts.length - 1] || documentFields.find(f => f.key === fieldKey)?.label || 'Document';
        }
        return '';
    };

    // Helper function to get document size
    const getDocumentSize = (doc) => {
        if (!doc) return '';
        if (doc instanceof File) {
            const sizeInKB = (doc.size / 1024).toFixed(2);
            return sizeInKB < 1024 ? `${sizeInKB} KB` : `${(doc.size / (1024 * 1024)).toFixed(2)} MB`;
        }
        return '';
    };

    // Truncate text with ellipsis
    const truncateText = (text, maxLength = 20) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className="section-container">
            <div className="card shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <div className="card-header" style={{
                    background: 'linear-gradient(135deg, #0d6efd 0%, #0056b3 100%)',
                    color: 'white',
                    padding: '1.25rem',
                    borderBottom: 'none'
                }}>
                    <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                        <i className="fas fa-paperclip me-2"></i>
                        Attach Documents
                    </h5>
                </div>
                <div className="card-body p-4">
                    {/* Notice about compulsory documents */}
                    <div className="alert alert-info mb-4" style={{
                        backgroundColor: '#e7f1ff',
                        border: '1px solid #0d6efd',
                        borderRadius: '10px',
                        padding: '1rem',
                        boxShadow: '0 2px 4px rgba(13, 110, 253, 0.1)'
                    }}>
                        <div className="d-flex align-items-center">
                            <i className="fas fa-info-circle me-3" style={{ fontSize: '1.3rem', color: '#0d6efd' }}></i>
                            <div>
                                <strong style={{ color: '#0d6efd', fontSize: '0.95rem' }}>
                                    Note: These documents are not mandatory but required for the exit process.
                                </strong>
                            </div>
                        </div>
                    </div>

                    {/* Document Upload Grid - Responsive 2-3 columns */}
                    <div className="row g-4">
                        {documentFields.map((field) => {
                            const hasFile = !!documents[field.key];
                            const isDragging = dragActive[field.key];
                            const progress = uploadProgress[field.key];
                            const error = errors[field.key];

                            return (
                                <div
                                    key={field.key}
                                    className="col-lg-4 col-md-6 col-sm-12"
                                >
                                    <div
                                        className="card h-100 shadow-sm document-upload-card"
                                        style={{
                                            border: hasFile
                                                ? '2px solid #28a745'
                                                : error
                                                    ? '2px solid #dc3545'
                                                    : isDragging
                                                        ? '2px dashed #0d6efd'
                                                        : '1px solid #dee2e6',
                                            borderRadius: '12px',
                                            transition: 'all 0.3s ease',
                                            overflow: 'hidden',
                                            backgroundColor: isDragging ? '#e7f1ff' : '#fff',
                                            position: 'relative'
                                        }}
                                        onDragEnter={(e) => handleDrag(e, field.key)}
                                        onDragLeave={(e) => handleDrag(e, field.key)}
                                        onDragOver={(e) => handleDrag(e, field.key)}
                                        onDrop={(e) => handleDrop(e, field.key)}
                                    >
                                        <div className="card-body p-3 d-flex flex-column" style={{ minHeight: '240px' }}>
                                            {/* Label */}
                                            <label
                                                className="form-label mb-3"
                                                style={{
                                                    fontSize: '0.9rem',
                                                    fontWeight: '600',
                                                    color: '#333',
                                                    textAlign: 'center',
                                                    marginBottom: '1rem',
                                                    minHeight: '2.5rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                {field.label}
                                                {field.required && <span className="text-danger ms-1">*</span>}
                                            </label>

                                            {/* Error Message */}
                                            {error && (
                                                <div className="alert alert-danger py-2 px-3 mb-3" style={{
                                                    fontSize: '0.75rem',
                                                    borderRadius: '6px',
                                                    marginBottom: '0.75rem'
                                                }}>
                                                    <i className="fas fa-exclamation-circle me-1"></i>
                                                    {error}
                                                </div>
                                            )}

                                            {/* Upload Progress */}
                                            {progress !== undefined && progress < 100 && (
                                                <div className="mb-3">
                                                    <div className="progress" style={{ height: '6px', borderRadius: '10px' }}>
                                                        <div
                                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                                            role="progressbar"
                                                            style={{ width: `${progress}%` }}
                                                        ></div>
                                                    </div>
                                                    <small className="text-muted d-block text-center mt-1" style={{ fontSize: '0.7rem' }}>
                                                        Uploading... {progress}%
                                                    </small>
                                                </div>
                                            )}

                                            {hasFile ? (
                                                // File uploaded - show preview
                                                <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                                                    {/* Preview Area */}
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            height: '100px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderRadius: '8px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginBottom: '0.75rem',
                                                            cursor: isImage(documents[field.key]) ? 'pointer' : 'default',
                                                            overflow: 'hidden',
                                                            border: '2px solid #e9ecef',
                                                            position: 'relative'
                                                        }}
                                                        onClick={() => isImage(documents[field.key]) && handleViewImage(
                                                            getDocumentUrl(documents[field.key]),
                                                            getDocumentName(documents[field.key], field.key)
                                                        )}
                                                        title={isImage(documents[field.key]) ? 'Click to view full size' : ''}
                                                    >
                                                        {isImage(documents[field.key]) ? (
                                                            <img
                                                                src={getDocumentUrl(documents[field.key])}
                                                                alt="preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.parentElement.innerHTML = '<i class="fas fa-file-pdf" style="font-size: 2rem; color: #dc3545;"></i>';
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="d-flex flex-column align-items-center">
                                                                <i className="fas fa-file-pdf" style={{ fontSize: '2rem', color: '#dc3545', marginBottom: '0.25rem' }}></i>
                                                                <small style={{ fontSize: '0.65rem', color: '#6c757d' }}>PDF</small>
                                                            </div>
                                                        )}
                                                        {/* Success Badge */}
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '6px',
                                                                left: '6px',
                                                                backgroundColor: '#28a745',
                                                                borderRadius: '50%',
                                                                width: '20px',
                                                                height: '20px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                                zIndex: 2
                                                            }}
                                                        >
                                                            <FaCheckCircle style={{ color: 'white', fontSize: '0.75rem' }} />
                                                        </div>
                                                        {/* Remove Button on Image */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveFile(field.key);
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                top: '6px',
                                                                right: '6px',
                                                                padding: '0.3rem 0.5rem',
                                                                backgroundColor: 'rgba(220, 53, 69, 0.9)',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                color: 'white',
                                                                transition: 'all 0.2s',
                                                                fontWeight: '500',
                                                                zIndex: 2,
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                                                minWidth: 'auto',
                                                                width: '28px',
                                                                height: '28px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#dc3545';
                                                                e.currentTarget.style.transform = 'scale(1.1)';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.9)';
                                                                e.currentTarget.style.transform = 'scale(1)';
                                                            }}
                                                            title="Remove file"
                                                        >
                                                            <FaTimes style={{ fontSize: '0.75rem' }} />
                                                        </button>
                                                    </div>

                                                    {/* File Info */}
                                                    <div className="text-center w-100 mb-2" style={{ minHeight: '2.5rem' }}>
                                                        <div
                                                            style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: '500',
                                                                color: '#333',
                                                                wordBreak: 'break-word',
                                                                marginBottom: '0.25rem'
                                                            }}
                                                            title={getDocumentName(documents[field.key], field.key)}
                                                        >
                                                            {truncateText(getDocumentName(documents[field.key], field.key), 20)}
                                                        </div>
                                                        {getDocumentSize(documents[field.key]) && (
                                                            <div style={{ fontSize: '0.65rem', color: '#6c757d' }}>
                                                                <i className="fas fa-file me-1"></i>
                                                                {getDocumentSize(documents[field.key])}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                // No file uploaded - show upload area with drag & drop
                                                <div
                                                    className="d-flex flex-column align-items-center justify-content-center w-100"
                                                    style={{
                                                        flex: 1,
                                                        minHeight: '180px',
                                                        border: isDragging ? '2px dashed #0d6efd' : '2px dashed #dee2e6',
                                                        borderRadius: '8px',
                                                        backgroundColor: isDragging ? '#e7f1ff' : '#f8f9fa',
                                                        transition: 'all 0.3s ease',
                                                        cursor: 'pointer'
                                                    }}
                                                    onDragEnter={(e) => handleDrag(e, field.key)}
                                                    onDragLeave={(e) => handleDrag(e, field.key)}
                                                    onDragOver={(e) => handleDrag(e, field.key)}
                                                    onDrop={(e) => handleDrop(e, field.key)}
                                                    onClick={() => {
                                                        const input = fileInputRefs.current[field.key];
                                                        if (input) input.click();
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        id={`file-${field.key}`}
                                                        ref={(el) => fileInputRefs.current[field.key] = el}
                                                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                                                        onChange={(e) => handleFileChange(field.key, e)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <div className="text-center p-3">
                                                        <FaFileUpload
                                                            style={{
                                                                fontSize: '2.5rem',
                                                                color: '#0d6efd',
                                                                marginBottom: '0.75rem',
                                                                transition: 'transform 0.2s'
                                                            }}
                                                            className={isDragging ? 'animate-bounce' : ''}
                                                        />
                                                        <div style={{
                                                            fontSize: '0.85rem',
                                                            color: '#0d6efd',
                                                            fontWeight: '600',
                                                            marginBottom: '0.4rem'
                                                        }}>
                                                            {isDragging ? 'Drop file here' : 'Upload'}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.7rem',
                                                            color: '#6c757d',
                                                            marginTop: '0.4rem',
                                                            lineHeight: '1.4'
                                                        }}>
                                                            JPG, PNG or PDF<br />
                                                            <span style={{ fontSize: '0.65rem' }}>(Max 5MB)</span>
                                                        </div>
                                                        {isDragging && (
                                                            <div style={{
                                                                fontSize: '0.65rem',
                                                                color: '#0d6efd',
                                                                marginTop: '0.4rem',
                                                                fontWeight: '500'
                                                            }}>
                                                                <i className="fas fa-hand-pointer me-1"></i>
                                                                Release to upload
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Image View Modal */}
            {viewImage && (
                <>
                    <div
                        className="modal-backdrop fade show"
                        style={{ zIndex: 10050, backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
                        onClick={handleCloseImageView}
                    ></div>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        style={{ zIndex: 10055 }}
                        onClick={handleCloseImageView}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '90vw' }}>
                            <div className="modal-content" style={{
                                opacity: 1,
                                backgroundColor: 'transparent',
                                border: 'none',
                                borderRadius: '12px'
                            }}>
                                <div className="modal-header" style={{
                                    border: 'none',
                                    justifyContent: 'space-between',
                                    padding: '1rem',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: '12px 12px 0 0'
                                }}>
                                    <h5 style={{ color: 'white', margin: 0, fontSize: '1rem', fontWeight: '500' }}>
                                        {viewImageName}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={handleCloseImageView}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                            borderRadius: '50%',
                                            padding: '0.5rem',
                                            opacity: 1
                                        }}
                                    ></button>
                                </div>
                                <div className="modal-body" style={{
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: '0 0 12px 12px'
                                }}>
                                    <img
                                        src={viewImage}
                                        alt={viewImageName}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '75vh',
                                            objectFit: 'contain',
                                            borderRadius: '8px',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div style="color: white; padding: 2rem; font-size: 1.1rem;">Image not available</div>';
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SectionG;
